import { supabase, unwrap, fetchAllRows, deleteRecordingFiles, apiPost } from '../supabase';
import { config } from '../config';
import { track, trackOnce } from '../analytics';
import { observePayment } from '../payment-analytics';
import type { PaddleOrder } from '../paddle-checkout';
import { openCheckout, type Prefill, type RazorpayOrder, type PaymentOutcome } from '../payment-checkout';
export type { PaymentOutcome } from '../payment-checkout';
import type { Currency, PlanCapacity, PlanEntitlements, QuoteMode } from '@shared/pricing';
import type { StorageBucket } from '../types';
import { viewerTimeZone } from '../dates';
import {
  clearRecordingReferences,
  deleteRecordingBatches,
  type RecordingDeletionProgress,
} from '../recording-deletion';

export type BreakdownType = 'month' | 'date' | 'employee';

export async function getStorageBreakdown(orgId: string, type: BreakdownType): Promise<StorageBucket[]> {
  const rows = unwrap(
    await supabase.rpc('get_storage_breakdown', { p_org_id: orgId, p_type: type, p_time_zone: viewerTimeZone() }),
  ) as Record<string, unknown>[] | null;
  return (rows ?? [])
    .map((r) => ({
      label: String(r.label ?? ''),
      size: Number(r.size) || 0,
      count: Number(r.count) || 0,
      employeeId: r.employee_id == null ? null : Number(r.employee_id),
    }))
    .filter((r) => r.label);
}

/** Per-month recording totals for one employee (the storage RPC can't filter by employee). */
export async function getEmployeeRecordingMonths(
  orgId: string,
  employeeId: number,
): Promise<StorageBucket[]> {
  const aggregated = await supabase.rpc('get_employee_recording_months', {
    p_org_id: orgId,
    p_employee_id: employeeId,
    p_time_zone: viewerTimeZone(),
  });
  if (!aggregated.error) {
    return ((aggregated.data ?? []) as Record<string, unknown>[]).map((row) => ({
      label: String(row.label),
      size: Number(row.size) || 0,
      count: Number(row.count) || 0,
    }));
  }
  // Retain the same results on older deployments while the additive RPC rolls out.
  if (!['PGRST202', '42883'].includes(aggregated.error.code)) unwrap(aggregated);
  const rows = await fetchAllRows<{ call_time: string | null; recording_size: number | null }>((from, to) =>
    supabase
      .from('call_log')
      .select('call_time, recording_size')
      .eq('org_id', orgId)
      .eq('employee_id', employeeId)
      .not('recording_url', 'is', null)
      .neq('recording_url', '')
      .order('id')
      .range(from, to),
  );
  const buckets = new Map<string, StorageBucket>();
  for (const r of rows) {
    const label = (r.call_time || '').slice(0, 7);
    if (!label) continue;
    const b = buckets.get(label) ?? { label, size: 0, count: 0 };
    b.size += Number(r.recording_size) || 0;
    b.count += 1;
    buckets.set(label, b);
  }
  return [...buckets.values()].sort((a, b) => b.label.localeCompare(a.label));
}

export interface RecordingScope {
  employeeId?: number;
  /** yyyy-MM in the viewer's time zone, like the storage breakdown */
  month?: string;
  /** yyyy-MM-dd in the viewer's time zone, like the storage breakdown */
  date?: string;
}

function localBounds(scope: RecordingScope): { from: string; to: string } | null {
  if (scope.month) {
    const [y, m] = scope.month.split('-').map(Number);
    return {
      from: new Date(y, m - 1, 1).toISOString(),
      to: new Date(y, m, 1).toISOString(),
    };
  }
  if (scope.date) {
    const [y, m, d] = scope.date.split('-').map(Number);
    return {
      from: new Date(y, m - 1, d).toISOString(),
      to: new Date(y, m - 1, d + 1).toISOString(),
    };
  }
  return null;
}

/** Deletes the audio files from storage and clears them from call logs. Returns how many were removed. */
export async function deleteRecordings(
  orgId: string,
  scope: RecordingScope,
  onProgress?: (progress: RecordingDeletionProgress) => void,
): Promise<number> {
  if (!scope.employeeId && !scope.month && !scope.date)
    throw new Error('Choose a recording group to delete.');
  onProgress?.({ phase: 'loading', deleted: 0, total: null });
  const bounds = localBounds(scope);
  const rows = await fetchAllRows<{ id: number; recording_url: string | null }>((from, to) => {
    let q = supabase
      .from('call_log')
      .select('id, recording_url')
      .eq('org_id', orgId)
      .not('recording_url', 'is', null)
      .neq('recording_url', '');
    if (scope.employeeId) q = q.eq('employee_id', scope.employeeId);
    if (bounds) q = q.gte('call_time', bounds.from).lt('call_time', bounds.to);
    return q.order('id').range(from, to);
  });

  const deleted = await deleteRecordingBatches(
    rows,
    {
      deleteFiles: (batch) =>
        deleteRecordingFiles(
          batch.map((r) => r.recording_url),
          { strict: true },
        ),
      clearRows: (batch) =>
        clearRecordingReferences(batch, {
          clear: async (row) => {
            unwrap(
              await supabase
                .from('call_log')
                .update({
                  recording_url: null,
                  recording_size: 0,
                  file_status: 'deleted',
                  updated_at: new Date().toISOString(),
                })
                .eq('org_id', orgId)
                .eq('id', row.id)
                .eq('recording_url', row.recording_url!),
            );
          },
          read: async (batch) =>
            unwrap(
              await supabase
                .from('call_log')
                .select('id, recording_url')
                .eq('org_id', orgId)
                .in(
                  'id',
                  batch.map((row) => row.id),
                ),
            ),
        }),
    },
    onProgress,
  );

  unwrap(await supabase.rpc('recalculate_org_storage', { p_org_id: orgId }));
  onProgress?.({ phase: 'complete', deleted, total: rows.length });
  return deleted;
}

export async function checkPromo(code: string): Promise<{ code: string; percent: number }> {
  const res = await apiPost<{ code: string; discount_percent: number }>('/api/billing/promo', {
    code: code.trim().toUpperCase(),
  });
  return { code: res.code, percent: Number(res.discount_percent) || 0 };
}

export interface PlanPurchase {
  mode: QuoteMode;
  users: number;
  storageGb: number;
  webhooks: boolean;
  termMonths: number;
  promoCode?: string;
}

let razorpayScript: Promise<void> | null = null;

function loadRazorpay(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  if (!razorpayScript) {
    razorpayScript = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        razorpayScript = null;
        reject(
          new Error(
            'Could not load the payment window. Check your connection or disable ad-blockers and try again.',
          ),
        );
      };
      document.body.appendChild(script);
    });
  }
  return razorpayScript;
}

const silentAnalytics = {
  track: (_event: string, _params: Record<string, unknown>) => {},
  once: (_key: string, _event: string, _params: Record<string, unknown>) => {},
};
const checkoutAnalytics = (key: string) =>
  key.startsWith('rzp_test_') ? silentAnalytics : { track, once: trackOnce };

async function createTrackedOrder(body: Record<string, unknown>): Promise<RazorpayOrder> {
  try {
    await loadRazorpay();
    return await apiPost<RazorpayOrder>('/api/razorpay/order', body);
  } catch (error) {
    track('checkout_order_failed', {
      product_type: body.kind,
      payment_provider: 'razorpay',
      reason: 'request_failed',
    });
    throw error;
  }
}

function purchaseBody(p: PlanPurchase) {
  return {
    kind: 'plan',
    mode: p.mode,
    users: p.users,
    storage_gb: p.storageGb,
    webhooks: p.webhooks,
    term_months: p.termMonths,
    promo_code: p.promoCode || '',
  };
}

export async function payForPlan(
  purchase: PlanPurchase,
  total: number,
  prefill: Prefill,
  onOpen?: () => Promise<void>,
  currency: Currency = 'INR',
): Promise<PaymentOutcome> {
  track('checkout_attempt', {
    product_type: 'plan',
    purchase_mode: purchase.mode,
    currency,
    value: total,
    term_months: purchase.termMonths,
  });
  if (total <= 0) {
    await apiPost('/api/billing/activate-free', purchaseBody(purchase));
    track('free_plan_activated', { product_type: 'plan', currency, value: 0 });
    return 'activated';
  }
  if (currency === 'USD') return payWithPaddle(purchaseBody(purchase), prefill, onOpen);
  const order = await createTrackedOrder(purchaseBody(purchase));
  return observePayment(
    {
      product_type: 'plan',
      purchase_mode: purchase.mode,
      users: purchase.users,
      storage_gb: purchase.storageGb,
      term_months: purchase.termMonths,
      transaction_id: order.order_id,
      value: order.amount / 100,
      currency: order.currency,
      payment_provider: 'razorpay',
    },
    (onFailure) =>
      openCheckout(order, purchase.mode === 'renew' ? 'CallSync plan' : 'CallSync add-ons', prefill, {
        keyId: config.razorpayKeyId,
        verify: (response) => apiPost('/api/razorpay/verify', response),
        onOpen,
        onFailure,
      }),
    checkoutAnalytics(order.key_id || config.razorpayKeyId),
  );
}

export async function buyCredits(
  credits: number,
  prefill: Prefill,
  onOpen?: () => Promise<void>,
  currency: Currency = 'INR',
): Promise<PaymentOutcome> {
  track('checkout_attempt', { product_type: 'credits', credits, currency });
  if (currency === 'USD') return payWithPaddle({ kind: 'credits', credits }, prefill, onOpen);
  const order = await createTrackedOrder({ kind: 'credits', credits });
  return observePayment(
    {
      product_type: 'credits',
      credits,
      transaction_id: order.order_id,
      value: order.amount / 100,
      currency: order.currency,
      payment_provider: 'razorpay',
    },
    (onFailure) =>
      openCheckout(order, `${credits.toLocaleString('en-IN')} AI analysis credits`, prefill, {
        keyId: config.razorpayKeyId,
        verify: (response) => apiPost('/api/razorpay/verify', response),
        onOpen,
        onFailure,
      }),
    checkoutAnalytics(order.key_id || config.razorpayKeyId),
  );
}

async function payWithPaddle(
  body: Record<string, unknown>,
  prefill: Prefill,
  onOpen?: () => Promise<void>,
): Promise<PaymentOutcome> {
  if (!config.paddleEnabled) {
    track('checkout_unavailable', { payment_provider: 'paddle' });
    throw new Error('International checkout is not available yet.');
  }
  const order = await apiPost<PaddleOrder>('/api/paddle/transaction', body);
  const params = {
    transaction_id: order.transaction_id,
    product_type: order.product_type,
    payment_provider: 'paddle',
    currency: 'USD',
    value: order.amount_cents / 100,
  };
  const analytics = order.environment === 'sandbox' ? silentAnalytics : { track, once: trackOnce };
  if (order.fulfilled) {
    analytics.once(`purchase:${order.transaction_id}`, 'purchase', params);
    return 'paid';
  }
  const { loadPaddle, openPaddleCheckout, openHostedPaddleCheckout } = await import('../paddle-checkout');
  // Live checkout runs on the Paddle-approved marketing site, framed over billing.
  const hosted =
    order.environment === 'live' && new URL(order.checkout_url).origin !== window.location.origin;
  if (!hosted) await loadPaddle();
  return observePayment(
    params,
    (onFailure) =>
      (hosted ? openHostedPaddleCheckout : openPaddleCheckout)(order, prefill, {
        verify: (transactionId) => apiPost('/api/paddle/verify', { transaction_id: transactionId }),
        onOpen,
        onFailure,
      }),
    analytics,
  );
}

/** Opens an existing, owner-verified Paddle transaction from a recovery or invoice link. */
export function resumePaddlePayment(transactionId: string, prefill: Prefill): Promise<PaymentOutcome> {
  return payWithPaddle({ transaction_id: transactionId }, prefill);
}

/** The hosted checkout returns here after Paddle reports success; only the backend can confirm it. */
export async function confirmPaddleReturn(transactionId: string): Promise<PaymentOutcome> {
  const { confirmPaddleTransaction } = await import('../paddle-checkout');
  await confirmPaddleTransaction(transactionId, (id) =>
    apiPost('/api/paddle/verify', { transaction_id: id }),
  );
  return payWithPaddle({ transaction_id: transactionId }, {});
}

/** International customers pay by PayPal manually; we record it as pending for review. */
export async function submitManualPayment(input: {
  orgId: string;
  userId: string;
  reference: string;
  amount: number;
  currency: Currency;
  entitlements: PlanEntitlements;
}): Promise<void> {
  unwrap(
    await supabase.from('upi_payments').insert({
      org_id: input.orgId,
      user_id: input.userId,
      utr: `PAYPAL-${input.reference.trim()}`,
      amount: input.amount,
      user_count: input.entitlements.user_count,
      storage_gb: input.entitlements.storage_gb,
      duration_months: input.entitlements.duration_months || 1,
      is_renewing: input.entitlements.is_renewing,
      status: 'pending',
      notes: JSON.stringify({ currency: input.currency, entitlements: input.entitlements }),
      created_at: new Date().toISOString(),
    }),
  );
}

export const PAYPAL_EMAIL = 'digiheadway@gmail.com';

/** Save or cancel a smaller setup for the next renewal; current capacity is untouched. */
export async function saveRenewalPlan(capacity: PlanCapacity | null): Promise<void> {
  await apiPost('/api/billing/renewal-plan', { capacity });
}
